/**
 * Audit Logger Service
 *
 * Comprehensive audit logging for permission checks, access attempts,
 * and security events. Provides both client-side logging (development)
 * and API integration for production audit trails.
 */

import type { Permission } from '../utils/permissions';
import type { OrganizationRole } from '../api/types';

/**
 * Audit event types
 */
export enum AuditEventType {
  PERMISSION_CHECK = 'permission_check',
  PERMISSION_DENIED = 'permission_denied',
  PERMISSION_GRANTED = 'permission_granted',
  ROUTE_ACCESS_DENIED = 'route_access_denied',
  ACTION_BLOCKED = 'action_blocked',
  ROLE_INSUFFICIENT = 'role_insufficient',
  ORGANIZATION_SWITCH = 'organization_switch',
  SECURITY_VIOLATION = 'security_violation',
}

/**
 * Audit event severity
 */
export enum AuditSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

/**
 * Audit log entry
 */
export interface AuditLogEntry {
  /**
   * Unique ID for this audit entry
   */
  id: string;

  /**
   * Timestamp (ISO 8601)
   */
  timestamp: string;

  /**
   * Type of audit event
   */
  eventType: AuditEventType;

  /**
   * Severity level
   */
  severity: AuditSeverity;

  /**
   * User ID who triggered the event
   */
  userId?: string;

  /**
   * User email
   */
  userEmail?: string;

  /**
   * User role in the current organization
   */
  userRole?: OrganizationRole;

  /**
   * Organization ID
   */
  organizationId?: string;

  /**
   * Organization name
   */
  organizationName?: string;

  /**
   * Permission that was checked
   */
  permission?: Permission | string;

  /**
   * Multiple permissions (for batch checks)
   */
  permissions?: Permission[];

  /**
   * Action that was attempted
   */
  action?: string;

  /**
   * Resource that was accessed (e.g., "/invoices/123")
   */
  resource?: string;

  /**
   * Resource type (e.g., "invoice", "account")
   */
  resourceType?: string;

  /**
   * Resource ID
   */
  resourceId?: string;

  /**
   * Result of the check (allowed/denied)
   */
  result: 'allowed' | 'denied';

  /**
   * Reason for denial
   */
  denialReason?: string;

  /**
   * IP address of the user
   */
  ipAddress?: string;

  /**
   * User agent string
   */
  userAgent?: string;

  /**
   * Session ID
   */
  sessionId?: string;

  /**
   * Additional context data
   */
  metadata?: Record<string, unknown>;
}

/**
 * Audit logger configuration
 */
interface AuditLoggerConfig {
  /**
   * Whether audit logging is enabled
   */
  enabled: boolean;

  /**
   * Whether to send logs to the API
   */
  sendToApi: boolean;

  /**
   * API endpoint for audit logs
   */
  apiEndpoint?: string;

  /**
   * Whether to store logs locally (for development)
   */
  storeLocally: boolean;

  /**
   * Maximum number of local logs to keep
   */
  maxLocalLogs: number;

  /**
   * Whether to log permission grants (can be noisy)
   */
  logGrants: boolean;

  /**
   * Whether to log all permission checks (very verbose)
   */
  logAllChecks: boolean;
}

/**
 * Audit Logger Class
 */
class AuditLogger {
  private config: AuditLoggerConfig;
  private localStorageKey = 'audit_logs';

  constructor() {
    // Default configuration
    this.config = {
      enabled: true,
      sendToApi: process.env.NODE_ENV === 'production',
      apiEndpoint: '/api/v1/audit/logs',
      storeLocally: process.env.NODE_ENV === 'development',
      maxLocalLogs: 500,
      logGrants: process.env.NODE_ENV === 'development',
      logAllChecks: false, // Too verbose, only enable for debugging
    };
  }

  /**
   * Configure the audit logger
   */
  public configure(config: Partial<AuditLoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Generate unique ID for audit entry
   */
  private generateId(): string {
    return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get client information
   */
  private getClientInfo(): Partial<AuditLogEntry> {
    return {
      userAgent: navigator.userAgent,
      // IP address would come from backend
      sessionId: sessionStorage.getItem('session_id') || undefined,
    };
  }

  /**
   * Log an audit event
   */
  public log(entry: Omit<AuditLogEntry, 'id' | 'timestamp'>): void {
    if (!this.config.enabled) {
      return;
    }

    // Skip grants if not configured to log them
    if (entry.result === 'allowed' && !this.config.logGrants) {
      return;
    }

    // Skip all checks if not configured
    if (entry.eventType === AuditEventType.PERMISSION_CHECK && !this.config.logAllChecks) {
      return;
    }

    // Create full audit entry
    const auditEntry: AuditLogEntry = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      ...this.getClientInfo(),
      ...entry,
    };

    // Store locally if configured
    if (this.config.storeLocally) {
      this.storeLocally(auditEntry);
    }

    // Send to API if configured
    if (this.config.sendToApi && this.config.apiEndpoint) {
      this.sendToApi(auditEntry);
    }

    // Console log in development
    if (process.env.NODE_ENV === 'development') {
      this.logToConsole(auditEntry);
    }
  }

  /**
   * Log to console (development only)
   */
  private logToConsole(entry: AuditLogEntry): void {
    const style = this.getConsoleStyle(entry.severity);
    const emoji = this.getSeverityEmoji(entry.severity);

    console.log(
      `%c${emoji} [AUDIT] ${entry.eventType}`,
      style,
      {
        result: entry.result,
        user: entry.userEmail,
        role: entry.userRole,
        org: entry.organizationName,
        permission: entry.permission,
        action: entry.action,
        resource: entry.resource,
        reason: entry.denialReason,
      }
    );
  }

  /**
   * Get console style based on severity
   */
  private getConsoleStyle(severity: AuditSeverity): string {
    switch (severity) {
      case AuditSeverity.CRITICAL:
        return 'color: #ff0000; font-weight: bold;';
      case AuditSeverity.ERROR:
        return 'color: #ff4444; font-weight: bold;';
      case AuditSeverity.WARNING:
        return 'color: #ff8800;';
      case AuditSeverity.INFO:
      default:
        return 'color: #0088ff;';
    }
  }

  /**
   * Get emoji for severity
   */
  private getSeverityEmoji(severity: AuditSeverity): string {
    switch (severity) {
      case AuditSeverity.CRITICAL:
        return '🚨';
      case AuditSeverity.ERROR:
        return '❌';
      case AuditSeverity.WARNING:
        return '⚠️';
      case AuditSeverity.INFO:
      default:
        return 'ℹ️';
    }
  }

  /**
   * Store audit log locally
   */
  private storeLocally(entry: AuditLogEntry): void {
    try {
      const logs = this.getLocalLogs();
      logs.push(entry);

      // Keep only the most recent logs
      if (logs.length > this.config.maxLocalLogs) {
        logs.splice(0, logs.length - this.config.maxLocalLogs);
      }

      localStorage.setItem(this.localStorageKey, JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to store audit log locally:', error);
    }
  }

  /**
   * Get local audit logs
   */
  public getLocalLogs(): AuditLogEntry[] {
    try {
      const logs = localStorage.getItem(this.localStorageKey);
      return logs ? JSON.parse(logs) : [];
    } catch (error) {
      console.error('Failed to retrieve local audit logs:', error);
      return [];
    }
  }

  /**
   * Clear local audit logs
   */
  public clearLocalLogs(): void {
    localStorage.removeItem(this.localStorageKey);
  }

  /**
   * Send audit log to API
   */
  private async sendToApi(entry: AuditLogEntry): Promise<void> {
    if (!this.config.apiEndpoint) {
      return;
    }

    try {
      // In production, this would send to your audit API
      // For now, we'll just log it
      console.info('[Audit API] Would send:', entry);

      // Example implementation:
      // await fetch(this.config.apiEndpoint, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(entry),
      // });
    } catch (error) {
      console.error('Failed to send audit log to API:', error);
    }
  }

  /**
   * Export logs as JSON
   */
  public exportLogs(): string {
    const logs = this.getLocalLogs();
    return JSON.stringify(logs, null, 2);
  }

  /**
   * Export logs as CSV
   */
  public exportLogsAsCSV(): string {
    const logs = this.getLocalLogs();
    if (logs.length === 0) {
      return '';
    }

    // CSV headers
    const headers = [
      'Timestamp',
      'Event Type',
      'Severity',
      'Result',
      'User',
      'Role',
      'Organization',
      'Permission',
      'Action',
      'Resource',
      'Denial Reason',
    ];

    // CSV rows
    const rows = logs.map((log) => [
      log.timestamp,
      log.eventType,
      log.severity,
      log.result,
      log.userEmail || '',
      log.userRole || '',
      log.organizationName || '',
      log.permission || '',
      log.action || '',
      log.resource || '',
      log.denialReason || '',
    ]);

    // Combine headers and rows
    return [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');
  }

  /**
   * Get statistics from logs
   */
  public getStatistics(): {
    total: number;
    denied: number;
    allowed: number;
    byEventType: Record<string, number>;
    bySeverity: Record<string, number>;
    topDeniedPermissions: Array<{ permission: string; count: number }>;
  } {
    const logs = this.getLocalLogs();

    const stats = {
      total: logs.length,
      denied: logs.filter((l) => l.result === 'denied').length,
      allowed: logs.filter((l) => l.result === 'allowed').length,
      byEventType: {} as Record<string, number>,
      bySeverity: {} as Record<string, number>,
      topDeniedPermissions: [] as Array<{ permission: string; count: number }>,
    };

    // Count by event type
    logs.forEach((log) => {
      stats.byEventType[log.eventType] = (stats.byEventType[log.eventType] || 0) + 1;
      stats.bySeverity[log.severity] = (stats.bySeverity[log.severity] || 0) + 1;
    });

    // Top denied permissions
    const deniedPermissions: Record<string, number> = {};
    logs
      .filter((log) => log.result === 'denied' && log.permission)
      .forEach((log) => {
        const perm = log.permission!;
        deniedPermissions[perm] = (deniedPermissions[perm] || 0) + 1;
      });

    stats.topDeniedPermissions = Object.entries(deniedPermissions)
      .map(([permission, count]) => ({ permission, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return stats;
  }
}

// Singleton instance
export const auditLogger = new AuditLogger();

export default auditLogger;
